import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Footer } from '../../../components/footers/Footer';
import { HomeHeader } from '../../../components/new/headers/HomeHeader';
import { PageMain } from '../../../components/seo/PageMain';
import { getNewsDetail } from '../api/api';
import {
  getMediaDisplaySrc,
  getNewsGraphic2Url,
  resolveContentMedia,
} from '../helper/graphic2';
import { DetailPageHelmet } from '../helper/DetailPageHelmet';
import { documentTitle, metaDescriptionFromRecord } from '../helper/detailSeo';
import { resolveNewsSourceHref } from '../helper/hubItemHref';
import { sanitizeRichHtml } from '../helper/sanitizeRichHtml';
import { getPublicSiteUrl } from '../siteUrl';

const NEWS_RICH_HTML_CLASS =
  'new-blog-content w-full max-w-none prose prose-base md:prose-lg';

function formatNewsDate(value) {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toDateString();
}

function isPdfUrl(value) {
  if (!value) return false;
  try {
    return new URL(value).pathname.toLowerCase().endsWith('.pdf');
  } catch {
    return false;
  }
}

function mapNewsRow(row) {
  return {
    postId: row.Id ?? row.id,
    title: row.Title,
    seoTitle: row.SeoTitle,
    metaDescription: row.MetaDescription,
    postDate: row.DatePublished || row.LastUpdated,
    postMedia: resolveContentMedia(row, getNewsGraphic2Url),
    postName: row.Relevance,
    description: row.Description,
    body: row.Body || row.Description || '',
    source: row.Source,
  };
}

export function NewsDetail() {
  const { newsName } = useParams();
  const [newsData, setNewsData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);

  const siteUrl = getPublicSiteUrl();
  const canonicalUrl = useMemo(() => {
    if (!siteUrl || !newsName) return '';
    return `${siteUrl}/articles/news/${encodeURIComponent(newsName)}`;
  }, [siteUrl, newsName]);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    setNewsData(null);
    setLoading(true);

    (async () => {
      if (!newsName) {
        if (!cancelled) {
          setLoadError('Missing news article');
          setLoading(false);
        }
        return;
      }

      try {
        const row = await getNewsDetail(newsName);
        if (!cancelled) setNewsData(mapNewsRow(row));
      } catch {
        if (!cancelled) setLoadError('News article not found');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [newsName]);

  const metaDescription = useMemo(() => {
    if (!newsData) return 'Charlee.ai news';
    return metaDescriptionFromRecord({
      metaDescription: newsData.metaDescription,
      description: newsData.description,
      body: newsData.body,
      title: newsData.title,
      fallback: 'Charlee.ai news',
    });
  }, [newsData]);

  const ogImage = useMemo(
    () => getMediaDisplaySrc(newsData?.postMedia),
    [newsData],
  );
  const pageTitle = newsData
    ? documentTitle(newsData.seoTitle, newsData.title, 'News')
    : 'News | Charlee.ai';
  const publishedDate = formatNewsDate(newsData?.postDate);
  const safeHtmlBody = useMemo(
    () => sanitizeRichHtml(newsData?.body || ''),
    [newsData?.body],
  );
  const hasHtmlBody = /<\/?[a-z][\s\S]*>/i.test(newsData?.body || '');
  const sourceUrl = resolveNewsSourceHref(newsData?.source) || '';
  const breadcrumbItems = useMemo(() => {
    if (!newsData?.title) return undefined;
    return [
      { name: 'Home', path: '/' },
      { name: 'News', path: '/articles/news' },
      { name: newsData.title, url: canonicalUrl },
    ];
  }, [newsData, canonicalUrl]);

  return (
    <div className="w-full overflow-hidden bg-[#F3F4F4]">
      {!loading && (
        <DetailPageHelmet
          pageTitle={pageTitle}
          metaDescription={metaDescription}
          canonicalUrl={canonicalUrl}
          ogImage={ogImage}
          ogType="article"
          noIndex={Boolean(loadError)}
          articlePublishedTime={newsData?.postDate}
          author="Charlee.ai"
          breadcrumbItems={breadcrumbItems}
          seoReady
        />
      )}

      <HomeHeader headerColor="#F9F9F9" textColor="#021744" />
      <PageMain className="news-page bg-[#F9F9F9]">
        {loading && (
          <div
            className="mt-16 flex min-h-[50vh] w-full items-center justify-center md:mt-24"
            role="status"
            aria-label="Loading"
          >
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-[#22c0b1] border-t-transparent" />
          </div>
        )}

        {!loading && loadError && (
          <div className="mt-20 min-h-[50vh] w-full px-4 text-center md:mt-32">
            <p className="text-lg text-[#021744]">{loadError}</p>
            <Link to="/articles/news" className="mt-4 inline-block text-[#22c0b1] underline">
              Back to news
            </Link>
          </div>
        )}

        {!loading && newsData && (
          <>
            <article className="mt-16 w-full md:mt-24">
              <div className="mx-auto w-full max-w-4xl px-4 sm:px-6">
                <div className="w-full pt-6 md:pt-14">
                  <h1 className="text-[28px] font-medium leading-[1.15] tracking-normal sm:text-[36px] sm:leading-[39.6px] md:text-[65px] md:leading-[71.5px]">
                    {newsData.title}
                  </h1>
                  <div className="flex w-full flex-wrap items-center justify-center gap-y-1 py-3 text-center text-sm text-[#0099B0] sm:py-2 sm:text-base">
                    {publishedDate ? <span className="px-3">{publishedDate}</span> : null}
                    <span className={publishedDate ? 'border-l border-l-[#0099B0] px-3' : 'px-3'}>
                      By Charlee.ai
                    </span>
                  </div>
                  <div className="w-full rounded-xl py-6 md:py-10">
                    <div className="w-full space-y-6 text-left md:space-y-8">
                      {ogImage ? (
                        <div className="w-full min-h-[220px] rounded-xl sm:min-h-[300px] md:min-h-[400px] lg:min-h-[500px]">
                          <img
                            src={ogImage}
                            alt={`${newsData.title} — Charlee.ai news`}
                            className="h-full min-h-[220px] w-full rounded-xl object-cover sm:min-h-[300px] md:min-h-[400px] lg:min-h-[500px]"
                            crossOrigin="anonymous"
                          />
                        </div>
                      ) : null}
                      {hasHtmlBody ? (
                        <div
                          className={NEWS_RICH_HTML_CLASS}
                          dangerouslySetInnerHTML={{ __html: safeHtmlBody }}
                        />
                      ) : (
                        <p className="new-blog-content mt-4 whitespace-pre-line text-base leading-7 text-[#021744] md:text-[18px] md:leading-[30px]">
                          {newsData.body}
                        </p>
                      )}
                      {sourceUrl ? (
                        <div className="pt-2">
                          <a
                            href={sourceUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex rounded-full bg-[#22c0b1] px-6 py-3 text-base font-semibold text-white no-underline hover:bg-[#021744]"
                          >
                            {isPdfUrl(sourceUrl) ? 'View Original PDF' : 'View Original Source'}
                          </a>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            </article>
            <div className="w-full bg-[#F3F4F4] py-12 md:py-20">
              <div className="mx-auto w-full max-w-6xl px-4 sm:px-6">
                <div className="flex w-full justify-start md:justify-end">
                  <Link
                    to="/articles/news"
                    className="w-full rounded-full border border-[#021744] px-6 py-3 text-center text-base font-medium leading-6 hover:bg-[#021744] hover:text-white sm:w-auto md:px-8 md:py-4 md:text-[18px]"
                  >
                    View all News
                  </Link>
                </div>
              </div>
            </div>
          </>
        )}
      </PageMain>
      <Footer />
    </div>
  );
}
